import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Input, Button, Space, Tag, Layout, Row, Typography, Modal, message, Switch } from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import axios from "../../services/axiosInstance";
import type { ColumnType } from "antd/es/table";
import { FETCH_ALL_DOCUMENTS, FETCH_SIGNED_DOCUMENTS, TOGGLE_REMINDER_EMAIL_AS_RENEWED } from "../../services/apiEndPoints";
import { IDatabase } from "../types/document.types";
import { imageInfo, snakeToCapitalCase } from "../upload/DualUpload";

const { Title, Link } = Typography;

export const isPdf = (url: string) => {
    return url.toLowerCase().includes(".pdf");
}

const DocumentTableComponent = () => {
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const [data, setData] = useState<IDatabase[]>([]);
    const searchInput = useRef<InputRef>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const fetchSignedUrl = async (s3Url: string) => {
        const response = await axios.get(
            FETCH_SIGNED_DOCUMENTS + `?s3Url=${encodeURIComponent(s3Url)}`);

        if (response?.data && response.data.results) {
            return response.data.results;
        }

        throw new Error("Failed to fetch signed URL");

    };

    const fetchDocuments = async () => {
        const response = await axios.get(FETCH_ALL_DOCUMENTS);

        if (response?.data && response.data.results) {
            setData(response.data.results);
        }
    };


    const handleRenewToggle = async (record: IDatabase) => {
        try {
            if (!record.isRenewed) {
                await axios.put(TOGGLE_REMINDER_EMAIL_AS_RENEWED(record.documentId), {});
                message.success("Renewal reminder set");
                await fetchDocuments();
            }
        } catch (error) {
            message.error("Failed to update renewal status");
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    const handleSearch = useMemo(
        () =>
            debounce((selectedKeys, confirm, dataIndex) => {
                confirm();
                setSearchText(selectedKeys[0]);
                setSearchedColumn(dataIndex);
            }, 300),
        []
    );

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };


    const getColumnSearchProps = (
        dataIndex: keyof IDatabase
    ): ColumnType<IDatabase> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(selectedKeys, confirm, dataIndex)
                    }
                    style={{ marginBottom: 8 }}
                />
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    >
                        Search
                    </Button>
                    <Button size="small" onClick={() => clearFilters?.()}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
        ),
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];
            if (!recordValue) return false;

            return recordValue
                .toString()
                .toLowerCase()
                .includes(String(value).toLowerCase());
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text || ""}
                />
            ) : (
                text
            ),
    });

    const columns = useMemo(
        () => [
            {
                title: "Type",
                dataIndex: "documentType",
                key: "documentType",
                ...getColumnSearchProps("documentType"),
            },
            {
                title: "Expiry Date",
                dataIndex: "expiryDate",
                key: "expiryDate",
                render: (date: string) =>
                    date ? moment(date).format("MMM D, YYYY") : "-",
            },
            {
                title: "S3 URL",
                dataIndex: "s3Url",
                key: "s3Url",
                render: (s3Url: string) =>
                    s3Url ? (
                        <Link color={"cyan"}
                            onClick={async () => {
                                try {
                                    setLoadingPreview(true);
                                    const url = await fetchSignedUrl(s3Url);
                                    setSignedUrl(url);
                                    setIsPreviewOpen(true);
                                } catch (error) {
                                    message.error("Failed to load document");
                                } finally {
                                    setLoadingPreview(false);
                                }
                            }}
                        >
                            View
                        </Link>
                    ) : (
                        "-"
                    ),
            },
            {
                title: "By",
                dataIndex: "uploadedBy",
                key: "uploadedBy",
            },
            {
                title: "Renewed",
                dataIndex: "isRenewed",
                key: "isRenewed",
                render: (_: boolean, record: IDatabase) => (
                    <Switch
                        checked={record.isRenewed}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        onChange={() => handleRenewToggle(record)}
                    />
                ),
            },
            {
                title: "Upload Date",
                dataIndex: "createdAt",
                key: "createdAt",
                render: (date: string) =>
                    date ? moment(date).format("MMM D, YYYY") : "-",
            },
            {
                title: "Meta",
                dataIndex: "meta",
                key: "meta",
                width: "50%",
                render: (meta: Record<string, any> | string[]) => {
                    if (!meta) return "-";

                    // If meta is an object → key:value tags
                    if (typeof meta === "object" && !Array.isArray(meta)) {
                        return imageInfo(meta).map(({ key, value }) => (
                            <Tag key={key} color="blue">
                                {snakeToCapitalCase(key)} :{String(value)}
                            </Tag>
                        ));
                    }

                    // If meta is an array
                    if (Array.isArray(meta)) {
                        return meta.map((item) => (
                            <Tag key={item} color="blue">
                                {item}
                            </Tag>
                        ));
                    }

                    return "-";
                },
            },
        ],
        [searchText, searchedColumn]
    );

    return (
        <Layout>
            <Modal
                open={isPreviewOpen}
                title="Document Preview"
                footer={null}
                onCancel={() => {
                    setIsPreviewOpen(false);
                    setSignedUrl(null);
                }}
                width="80%"
                destroyOnClose
            >
                {loadingPreview && signedUrl ? (
                    <div style={{ textAlign: "center" }}>Loading...</div>
                ) : signedUrl && isPdf(signedUrl) ? (
                    <iframe src={signedUrl} width="100%" style={{ height: "75vh" }}></iframe>
                ) : <img
                    src={signedUrl || ""}
                    alt="Document Preview"
                    style={{
                        maxWidth: "100%",
                        maxHeight: "75vh",
                        objectFit: "contain",
                        display: "block",
                        margin: "0 auto",
                    }}
                />}
            </Modal>
            <Row justify="space-between" align="middle">
                <Title level={1} style={{ margin: "16px 0" }}>
                    Document Directory
                </Title>
            </Row>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={data}
                pagination={{ pageSize: 10 }}
            />
        </Layout >
    );
};

export default DocumentTableComponent;
